import React, { useEffect, useMemo, useState } from "react";
import styles from "./AgencyListCard.module.css";
import axios from "axios";

const defaultProfileImage = "https://via.placeholder.com/150";

function AgencyListCard({ agencyData = {}, openAgency }) {
  const [employees, setEmployees] = useState([]);
  const [listings, setListings] = useState([]);

  useEffect(() => {
    const id = agencyData?.id;
    if (!id) {
      setEmployees([]);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    (async () => {
      try {
        const res = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/agency/${id}/professionals`,
          { signal: controller.signal }
        );
        if (isMounted && res.status === 200 && res.data?.success) {
          setEmployees(res.data.professionals ?? []);
        } else if (isMounted) {
          setEmployees([]);
        }
      } catch (err) {
        // Ignore abort errors; log others
        if (err.name !== "CanceledError") {
          console.error("Failed to load professionals:", err);
        }
        if (isMounted) setEmployees([]);
      }
    })();
  });

  const {
    id,
    agency_name,
    phone,
    state,
    city,
    email,
    established_year,
    logo_url,

    // NEW: metrics (numbers)
    listings_volume, // e.g. 71500000
    total_listings, // e.g. 186
    total_employees, // e.g. 5.0
  } = agencyData;

  const logoSrc = logo_url || defaultProfileImage;

  // Years of experience
  const yearsOfExperience = useMemo(() => {
    if (!established_year) return "N/A";
    const d = new Date(established_year);
    if (isNaN(d.getTime())) return "N/A";
    const now = new Date();
    let years = now.getFullYear() - d.getFullYear();
    const hasHadAnniversary =
      now.getMonth() > d.getMonth() ||
      (now.getMonth() === d.getMonth() && now.getDate() >= d.getDate());
    if (!hasHadAnniversary) years -= 1;
    return years >= 0 ? years : "N/A";
  }, [established_year]);

  // --------- KPI formatters ----------
  const formatCurrencyShort = (num) => {
    if (num == null || isNaN(num)) return "—";
    const abs = Math.abs(num);
    if (abs >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `$${(num / 1e3).toFixed(1)}K`;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const totalListingVolme = useMemo(
    () => formatCurrencyShort(listings_volume),
    [listings_volume]
  );
  const totalListings = useMemo(
    () =>
      typeof total_listings === "number"
        ? total_listings.toLocaleString()
        : "—",
    [total_listings]
  );
  const totalEmployees = useMemo(
    () =>
      typeof total_employees === "number" ? total_employees.toFixed(1) : "—",
    [total_employees]
  );

  return (
    <div className={styles.cardContainer}>
      <div className={styles.cardWrapper}>
        <div
          className={styles.cardInner}
          onClick={() => openAgency?.(id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" ? openAgency?.(id) : null)}
        >
          <div className={styles.innerCardFrame}>
            <div className={styles.contentGrid}>
              {/* Left: logo */}
              <div className={styles.logoCol}>
                <div className={styles.logoBox}>
                  <img src={logoSrc} alt={`${agency_name || "Agency"} logo`} />
                </div>
              </div>

              {/* Right: details */}
              <div className={styles.infoCol}>
                <h3 className={styles.name}>{agency_name || "Agency Name"}</h3>

                <p className={styles.line}>{phone || "N/A"}</p>
                <p className={styles.line}>
                  {state || city
                    ? `${city ? city + ", " : ""}${state || ""}`
                    : "N/A"}
                </p>
                <a
                  className={styles.email}
                  href={email ? `mailto:${email}` : undefined}
                  onClick={(e) => {
                    if (!email) e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {email || "N/A"}
                </a>
                <p className={styles.line}>
                  Years of Experience: {yearsOfExperience}
                </p>

                {/* NEW: KPI strip */}
                <div className={styles.statsRow} aria-label="Agency metrics">
                  <div className={styles.statBlock}>
                    <div className={styles.statValue}>{totalListingVolme}</div>
                    <div className={styles.statLabel}>Listings Volume</div>
                  </div>
                  <div className={styles.statBlock}>
                    <div className={styles.statValue}>{totalListings}</div>
                    <div className={styles.statLabel}>Total Listings</div>
                  </div>
                  <div className={styles.statBlock}>
                    <div className={styles.statValue}>{totalEmployees}</div>
                    <div className={styles.statLabel}>Total Employees</div>
                  </div>
                </div>

                <button
                  className={styles.contactBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    // wire your contact action here
                  }}
                >
                  Contact
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgencyListCard;
